// currentTenanat-overview-tab.tsx
import React, { useEffect, useState } from "react";
import "../../../../newComponents/charts/expense-distribution/expense-distribution.css";
import NOIChart from "../../../charts/noi-chart/noi-chart";
import DonutChart from "../../../charts/revenue-overview/revenue-overview";
import { DateFilterTypesEnum, TenantService, EntityType, PaymentSummaryVM, OrganizationService, FilterEntityEnum } from "@propertelligent/client-api";
import { useParams } from "react-router-dom";
import apiHelper from "../../../../utils/apiHelper";
import { UserLoginData } from "../../../../components/common/charts/Enums";
import SingleChatMessages from "../../../SingleChatBox/SingleChatmessages";
import LastTransactions from "../../../charts/last-transactions/last-transactions";
import AddPaymentModal from "../../../payments/AddPaymentModal";

interface CurrentTenantOverviewTabContentProps {
  entityTypeId?: EntityType;
  onPaymentAction?: () => void;
}

const CurrentTenantOverviewTabContent: React.FC<CurrentTenantOverviewTabContentProps> = ({ entityTypeId, onPaymentAction }) => {
  const { id, unitId } = useParams();
  const [noiLoading, setNoiLoading] = useState(false);
  const [psLoading, setPSLoading] = useState(false);
  const [noiIsEmpty, setNoiIsEmpty] = useState(true);
  const orgId = parseInt(sessionStorage.getItem(UserLoginData.organization_id) || '0');

  // Add state variables
  const [transactionPropertyList, setTransactionPropertyList] = useState<any[]>([]);
  const [transactionPropertyLoading, setTransactionPropertyLoading] = useState(true);

  // 2. Add Modal State
  const [paymentModalConfig, setPaymentModalConfig] = useState({
    isOpen: false,
    entityId: null as number | null,
    entityType: EntityType.Tenant
  });

  const [noiData, setNoiData] = useState({
    totalRevenue: 0,
    totalRevenueFormatted: "0",
    totalRevenueRaw: "0",
    totalExpenses: 0,
    totalExpensesFormatted: "0",
    totalExpensesRaw: "0",
    netProfit: 0,
    netProfitFormatted: "0",
    netProfitRaw: "0",
  });

  const [paymentSummary, setPaymentSummary] = useState<any>({
    OnTimePaymentsPercent: "0",
    OnTimePaymentsPercentRaw: "0",

    OnTimePaymentCount: "0",
    OnTimePaymentCountRaw: "0",

    LatePaymentsCount: "0",
    LatePaymentsCountRaw: "0",

    CurrentOverDuePayments: "0",
    CurrentOverDuePaymentsRaw: "0"

  });

  const [noiDateValue, setNoiDateValue] = useState("Last 12 Month");
  const [paymentDateValue, setPaymentDateValue] = useState("Last 12 Month");

  const timeRangeToEnumMap: { [key: string]: number } = {
    "Last 12 Month": DateFilterTypesEnum.Last12Months,
    "Last 6 Month": DateFilterTypesEnum.Last6Months,
    "Last 3 Month": DateFilterTypesEnum.Last3Months,
    "This Month": DateFilterTypesEnum.LastMonth,
  };

  // ✅ Fetch NOI stats with date filter
  const getNOIStats = async (selectedRange?: string) => {
    setNoiLoading(true);

    const rangeToUse = selectedRange || noiDateValue;
    const filterEnum = timeRangeToEnumMap[rangeToUse];

    try {
      const res = await apiHelper(
        OrganizationService.getApiOrganizationTransactionOrganizationlist,
        { showNotification: false },
        orgId,
        undefined,          // pageSize
        undefined,          // pageNumber
        undefined,          // skipValue
        FilterEntityEnum.Tenant, // filterEntityTypeId
        id ? [Number(id)] : undefined, // filterEntityIds - Passed as Array<number>
        undefined,          // fullyQualifiedFilters (REMOVED String Filter)
        undefined,          // fullyQualifiedSorts
        filterEnum,         // dateFilterDateFilterTypeId (Arg 9)
        new Date().toISOString() // dateFilterClientDate (Arg 10)
      );

      const rawItems = res?.data;

      if (rawItems.success.length > 0) {
        setNoiIsEmpty(false);
        setNoiData({
          totalRevenue: rawItems?.aggregateValues?.TotalRevenue || 0,
          totalRevenueFormatted: rawItems?.formattedAggregateValues?.TotalRevenueFormatted || "0",
          totalRevenueRaw: rawItems?.formattedAggregateValues?.TotalRevenueRaw || "0",
          totalExpenses: rawItems?.aggregateValues?.TotalExpense || 0,
          totalExpensesFormatted: rawItems?.formattedAggregateValues?.TotalExpenseFormatted || "0",
          totalExpensesRaw: rawItems?.formattedAggregateValues?.TotalExpenseRaw || "0",
          netProfit: rawItems?.calculatedValues?.NetProfit || 0,
          netProfitFormatted: rawItems?.formattedCalculatedValues?.NetProfitFormatted || "0",
          netProfitRaw: rawItems?.formattedCalculatedValues?.NetProfitRaw || "0",
        });
      }

    } catch (err) {
      console.error("NOI API Error:", err);
    } finally {
      setNoiLoading(false);
    }
  };

  // ✅ Fetch Payment Summary stats (Updated with Date Filter)
  const getPaymentSummaryStats = async (selectedRange?: string) => {
    setPSLoading(true);

    const rangeToUse = selectedRange || paymentDateValue;
    const filterEnum = timeRangeToEnumMap[rangeToUse] || DateFilterTypesEnum.Last12Months;

    try {
      const res = await apiHelper(
        TenantService.getApiTenantPaymentSummary,
        { showNotification: false },
        Number(id),           // tenantId
        filterEnum,           // dateFilterTypeId (Arg 2)
        new Date().toISOString() // clientDate (Arg 3)
      );

      const formattedItems = res?.data?.success?.formatted;
      if (formattedItems) {
        setPaymentSummary({
          OnTimePaymentsPercent: formattedItems.OnTimePaymentsPercent || "0",
          OnTimePaymentsPercentRaw: formattedItems.OnTimePaymentsPercentRaw || "0",

          OnTimePaymentCount: formattedItems.OnTimePaymentCount || "0",
          OnTimePaymentCountRaw: formattedItems.OnTimePaymentCountRaw || "0",

          LatePaymentsCount: formattedItems.LatePaymentsCount || "0",
          LatePaymentsCountRaw: formattedItems.LatePaymentsCountRaw || "0",

          CurrentOverDuePayments: formattedItems.CurrentOverDuePayments || "0",
          CurrentOverDuePaymentsRaw: formattedItems.CurrentOverDuePaymentsRaw || "0",
        });
      }
    } catch (err) {
      console.error("Payment Summary API Error:", err);
    } finally {
      setPSLoading(false);
    }
  };

  // ✅ Payment Summary - Refetches when date filter or ID changes
  useEffect(() => {
    if (id) {
      getPaymentSummaryStats();
    }
  }, [id, paymentDateValue]);

  // ✅ NOI data filter change par load ho
  useEffect(() => {
    if (id && unitId) {
      getNOIStats();
    }
  }, [noiDateValue, id, unitId]);

  // ✅ Handle time range change for NOI & Payment Summary
  const handleNoiTimeRangeChange = (newRange: string) => {
    setNoiDateValue(newRange);
  };

  const handlePaymentTimeRangeChange = (newRange: string) => {
    setPaymentDateValue(newRange);
  };

  const formattedTransactions = transactionPropertyList.map((transaction: any) => ({
    id: transaction.id.toString(),
    type: transaction.description || "Rent",
    person: transaction.payeeAccountName || "Unknown",
    unit: transaction.unitCode || "N/A",
    amount: transaction.formatted.Amount || 0,
    amountRaw: transaction.formatted.AmountRaw || 0,
    date: transaction.formatted.PaymentDateDateOnly
  }));

  const getTransactionPropertyList = () => {
    setTransactionPropertyLoading(true);

    // Call the API for property transaction list
    apiHelper(
      OrganizationService.getApiOrganizationTransactionTenantlist,
      { showNotification: false },
      Number(id), // TenantId
      5,          // pageSize
      1,          // pageNumber
      undefined,  // skipValue
      2,          // filterEntityTypeId
      Number(id), // filterEntityIds
      null,       // fullyQualifiedFilters
      "PaymentDate desc" // fullyQualifiedSorts
    )
      .then((res) => {
        const rawItems = res?.data?.success;

        if (Array.isArray(rawItems)) {
          setTransactionPropertyList(rawItems);
        } else {
          setTransactionPropertyList([]);
        }

        setTransactionPropertyLoading(false);
      })
      .catch((err) => {
        console.error("API Error:", err);
        setTransactionPropertyList([]);
        setTransactionPropertyLoading(false);
      });
  };

  // Call the API when component mounts or when any dependent value changes
  useEffect(() => {
    getTransactionPropertyList();
  }, [id, entityTypeId]);

  return (
    <>
      <div className="top-listing-header-top" style={{ paddingTop: "24px" }}>
        <h2 className="H2">Overview</h2>
      </div>

      <div className="row">
        <div className="col-6">
          <NOIChart
            headerTitle="NOI From Tenant"
            dateFilter={noiDateValue}
            totalRevenue={noiData.totalRevenue}
            totalRevenueFormatted={noiData.totalRevenueFormatted}
            totalRevenueRaw={noiData.totalRevenueRaw}
            netProfit={noiData.netProfit}
            netProfitFormatted={noiData.netProfitFormatted}
            netProfitRaw={noiData.netProfitRaw}
            expenseAmount={noiData.totalExpenses}
            expenseAmountFormatted={noiData.totalExpensesFormatted}
            expenseAmountRaw={noiData.totalExpensesRaw}
            onTimeRangeChange={handleNoiTimeRangeChange}
            isLoading={noiLoading}
            noiIsEmpty={noiIsEmpty}
          />
        </div>

        <div className="col-6">
          <DonutChart
            totalName="On Time Payment"
            heading="Payment Summary"
            data={[
              { title: "On-time Payments", value: paymentSummary.OnTimePaymentCountRaw ?? 0, formattedValue: paymentSummary.OnTimePaymentCount || "0", color: "#1A56DB" },
              { title: "Late Payments", value: paymentSummary.LatePaymentsCountRaw ?? 0, formattedValue: paymentSummary.LatePaymentsCount || "0", color: "#E02424" },
            ]}
            valueTotal={paymentSummary.OnTimePaymentsPercentRaw ?? 0}
            valueSymbol="%"
            symbolPosition="after"
            isAlertStrip={true}
            alertStripText="Current Over Due Payments"
            alertStripSubText={paymentSummary.CurrentOverDuePayments || "0"}
            onTimeRangeChange={handlePaymentTimeRangeChange}
            dateFilter={paymentDateValue}
            isLoading={psLoading}
            isCurrency={false}
          />
        </div>
        <div className="col-6">
          <LastTransactions
            transactions={formattedTransactions}
            entityId={Number(id)}
            isLoading={transactionPropertyLoading}
            // 3. Pass Handler to Open Modal
            onAddPayment={() => setPaymentModalConfig({
              isOpen: true,
              entityId: Number(id),
              entityType: EntityType.Tenant
            })}
          />
        </div>

        <div className="col-6">
          <SingleChatMessages tenantId={parseInt(id || '0')} />
        </div>
      </div>

      {/* 4. Render Add Payment Modal */}
      {paymentModalConfig.isOpen && (
        <AddPaymentModal
          isOpen={paymentModalConfig.isOpen}
          onClose={() => {
            setPaymentModalConfig(prev => ({ ...prev, isOpen: false }));
            getTransactionPropertyList(); // Refresh transactions locally

            if (onPaymentAction) {
              onPaymentAction();
            }
          }}
          entityIdProp={paymentModalConfig.entityId || undefined}
          entityTypeIdProp={paymentModalConfig.entityType}
          defaultPaymentMode="2" // Default for Tenants (Receivables)
        />
      )}
    </>
  );
};

export default CurrentTenantOverviewTabContent;